import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
  Fade,
  Alert,
  Collapse
} from '@mui/material';
// import { API_CONFIG, ZATCA_CONFIG } from '../utils/config';
import Checklist from '../components/ZatcaOnboarding/Checklist';
import OTPGeneration from '../components/ZatcaOnboarding/OTPGeneration';
import CompanyDetails from '../components/ZatcaOnboarding/CompanyDetails';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import axios from 'axios';
import { API_CONFIG } from '../utils/config';
import { useAuth } from '../context/AuthContext';
import Lottie from 'lottie-react';
import loadingAnimation from '../assets/loading-animation.json';

const steps = [
  { label: 'Checklist', labelAr: 'قائمة التحقق' },
  { label: 'OTP Generation', labelAr: 'إنشاء رمز OTP' },
  { label: 'Company Details', labelAr: 'تفاصيل الشركة' }
];

function ZatcaOnboarding() {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    otp: '',
    companyDetails: {
      commonName: '',
      esgSerialNumber: '',
      organizationIdentifier: '',
      organizationUnitName: '',
      organizationName: '',
      country: 'SA',
      invoiceType: '1100',
      industry: '',
      location: '',
    }
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [onboardingResults, setOnboardingResults] = useState(null);
  const navigate = useNavigate();
  const { currentTenant } = useAuth();

  useEffect(() => {
    const fetchSerialNumber = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_CONFIG.API_URL}/certificates/generate-serial`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.data && response.data.success && response.data.data && response.data.data.serialNumber) {
          setFormData(prev => ({
            ...prev,
            companyDetails: {
              ...prev.companyDetails,
              esgSerialNumber: response.data.data.serialNumber
            }
          }));
        }
      } catch (err) {
        console.error('Failed to fetch ESG serial number:', err);
      }
    };

    fetchSerialNumber();
  }, []);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setSuccess(false);
    setError(null);

    try {
      // Get tenant ID - handle both _id and id formats
      const tenantId = currentTenant?._id || currentTenant?.id;

      if (!tenantId) {
        console.error('Current tenant:', currentTenant);
        setError('Company information not found. Please login again.');
        setLoading(false);
        return;
      }

      console.log('Using tenant ID:', tenantId);

      // Validate required fields
      if (!formData.companyDetails.commonName || !formData.companyDetails.esgSerialNumber ||
        !formData.companyDetails.organizationIdentifier ||
        !formData.companyDetails.organizationUnitName || !formData.companyDetails.organizationName ||
        !formData.companyDetails.location || !formData.companyDetails.industry) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      // Validate Organization Identifier (VAT Number)
      const vatNumber = formData.companyDetails.organizationIdentifier;
      if (!/^3\d{13}3$/.test(vatNumber)) {
        setError('Organization Identifier (VAT Number) must be exactly 15 digits long and start and end with the digit 3.');
        setLoading(false);
        return;
      }

      // Validate OTP
      const otpValue = formData.companyDetails.otp || '';
      if (otpValue.length !== 6) {
        setError('Please enter a valid 6-digit OTP');
        setLoading(false);
        return;
      }

      // Get token for authentication
      const token = localStorage.getItem('token');

      // Prepare request body with logged-in company ID
      const requestBody = {
        companyId: tenantId,
        commonName: formData.companyDetails.commonName,
        serialNumber: formData.companyDetails.esgSerialNumber,
        organizationIdentifier: formData.companyDetails.organizationIdentifier,
        organizationUnitName: formData.companyDetails.organizationUnitName,
        organizationName: formData.companyDetails.organizationName,
        countryName: formData.companyDetails.country,
        invoiceType: formData.companyDetails.invoiceType,
        locationAddress: formData.companyDetails.location,
        industryBusinessCategory: formData.companyDetails.industry,
        OTP: otpValue
      };

      console.log('Submitting onboarding request to Python API:', API_CONFIG.ZATCA_API_URL);
      console.log('Request body:', requestBody);

      // Get environment type from tenant settings or default to Production
      const environmentType = currentTenant?.settings?.zatcaEnvironmentType || 'NonProduction';

      console.log('Using environment type:', environmentType);

      // Make API call to Python ZATCA API (port 5001)
      const response = await axios.post(
        `${API_CONFIG.ZATCA_API_URL}/zatca/onboarding/complete`,
        requestBody,
        {
          headers: {
            'X-Environment-Type': "Production",
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      console.log('Onboarding response:', response.data);

      if (response.data.success) {
        setOnboardingResults(response.data.results);
        setSuccess(true);

        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } else {
        setError(response.data.message || 'Onboarding failed. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Onboarding error:', err);

      // Handle different error types
      let errorMessage = 'Failed to complete onboarding. Please try again.';

      if (err.response) {
        // Server responded with error
        if (err.response.status === 400) {
          errorMessage = err.response.data.message || 'Invalid request. Please check all fields.';
        } else if (err.response.status === 401) {
          errorMessage = 'Authentication failed. Please login again.';
        } else if (err.response.status === 403) {
          errorMessage = 'You do not have permission to perform this action.';
        } else if (err.response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = err.response.data.message || errorMessage;
        }
      } else if (err.request) {
        // Request made but no response
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        // Something else happened
        errorMessage = err.message || errorMessage;
      }

      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleFormChange = (section) => (data) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], ...data },
    }));
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <Checklist onComplete={handleNext} />;
      case 1:
        return <OTPGeneration onComplete={handleNext} />;
      case 2:
        return <CompanyDetails data={formData} onChange={handleFormChange} onSubmit={handleSubmit} />;
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{
      width: '100%',
      minHeight: '100vh',
      bgcolor: '#ffffff',
      py: 3,
      position: 'relative'
    }}>
      <Container maxWidth="xl" sx={{ height: '100%' }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 0,
            border: '1px solid',
            borderColor: 'divider',
            height: '100%',
            bgcolor: 'background.paper',
            position: 'relative'
          }}
        >
          {/* Overlay for loading and success */}
          {(loading || success) && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                bgcolor: 'rgba(255,255,255,0.95)',
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s',
                p: 4
              }}
            >
              {loading && (
                <Box sx={{ textAlign: 'center' }}>
                  <Lottie
                    animationData={loadingAnimation}
                    loop={true}
                    style={{ width: 200, height: 200, margin: '0 auto' }}
                  />
                  <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
                    Processing onboarding request...
                  </Typography>
                </Box>
              )}
              {success && (
                <Fade in={success} unmountOnExit>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: 600 }}>
                    <CheckCircleIcon
                      color="success"
                      sx={{
                        fontSize: 56,
                        mb: 2,
                        color: '#2e7d32',
                        animation: 'scaleIn 0.5s ease-out'
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: '#2e7d32',
                        animation: 'fadeIn 0.5s ease-out',
                        mb: 2
                      }}
                    >
                      Production CSID Obtained and Stored Securely!
                    </Typography>

                    {onboardingResults && (
                      <Box sx={{ width: '100%', mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          Onboarding Summary:
                        </Typography>
                        {onboardingResults.steps.map((step, index) => (
                          <Box key={index} sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                            <CheckCircleIcon sx={{ fontSize: 16, color: '#2e7d32', mr: 1 }} />
                            <Typography variant="body2">
                              Step {step.step}: {step.name} - {step.message}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                      Redirecting to dashboard...
                    </Typography>
                  </Box>
                </Fade>
              )}
            </Box>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 0.5
              }}
            >
              ZATCA Onboarding
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: 'text.secondary',
                fontWeight: 400,
                fontSize: '0.875rem'
              }}
            >
              Complete the ZATCA onboarding process to enable e-invoicing
            </Typography>
          </Box>

          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              mb: 4,
              '& .MuiStepLabel-label': {
                fontSize: '0.875rem',
                fontWeight: 500
              }
            }}
          >
            {steps.map((step, index) => (
              <Step key={index}>
                <StepLabel>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {step.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        mt: 0.5
                      }}
                    >
                      {step.labelAr}
                    </Typography>
                  </Box>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ mt: 4, mb: 4 }}>
            {/* Error Alert */}
            <Collapse in={!!error}>
              <Alert
                severity="error"
                icon={<ErrorIcon />}
                onClose={() => setError(null)}
                sx={{ mb: 2, borderRadius: 0 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {error}
                </Typography>
              </Alert>
            </Collapse>

            {getStepContent(activeStep)}
          </Box>

          {activeStep === 2 && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 4,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              minHeight: 80
            }}>
              <Button
                onClick={handleBack}
                sx={{
                  mr: 1,
                  px: 3,
                  borderRadius: 0,
                  textTransform: 'none',
                  fontWeight: 500,
                  height: '36px'
                }}
                disabled={loading || success}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                sx={{
                  px: 3,
                  borderRadius: 0,
                  textTransform: 'none',
                  fontWeight: 500,
                  height: '36px'
                }}
                disabled={loading || success}
              >
                Submit
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
      <style>
        {`
          @keyframes scaleIn {
            0% {
              transform: scale(0);
              opacity: 0;
            }
            50% {
              transform: scale(1.2);
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
          @keyframes fadeIn {
            0% {
              opacity: 0;
              transform: translateY(10px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </Box>
  );
}

export default ZatcaOnboarding; 